// Shared fuzzy, multi-word item-name search used by ItemsList (Items page),
// ItemCards (POS main screen) and StockDetails, so typing roughly the right
// words finds an item regardless of word order, extra spaces, or a small
// typo. Barcode matching is untouched wherever this is used - every caller
// still does its own plain substring check on item.barcode separately.

const levenshtein = (a, b) => {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = new Array(n + 1);
  for (let j = 0; j <= n; j++) dp[j] = j;
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const temp = dp[j];
      dp[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j], dp[j - 1]);
      prev = temp;
    }
  }
  return dp[n];
};

// Typo budget scaled to word length: a one-letter slip on a short word
// shouldn't match everything, but a longer word can absorb a couple of
// mistyped/transposed letters.
const typoBudget = (len) => (len <= 4 ? 1 : len <= 7 ? 2 : 3);

const wordMatches = (queryWord, itemWord) => {
  if (!queryWord) return true;
  if (itemWord.includes(queryWord)) return true;
  if (queryWord.length < 3) return false; // too short to fuzzy-match safely
  return levenshtein(queryWord, itemWord) <= typoBudget(queryWord.length);
};

/**
 * True if every word in `query` fuzzy-matches at least one word in
 * `itemName` - case-insensitive, any word order, tolerant of extra spaces
 * and small typos, and of partial/prefix words (e.g. "oni" -> "onion").
 */
export const matchesItemName = (itemName, query) => {
  const queryWords = String(query || "").toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (!queryWords.length) return true;
  const itemWords = String(itemName || "").toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (!itemWords.length) return false;
  return queryWords.every((qw) => itemWords.some((iw) => wordMatches(qw, iw)));
};
